<template>
	<visible-transition :duration="500" @before-leave="leaving = true" @after-leave="leaving = false">
		<!-- There's a design requirement to have the header outside of the background, hence the double sections -->
		<section v-show="showOrder || showPayment" class="cog-payment cog-section" :class="'cog-payment--' + paymentProcessor">
			<div class="cog-section__inner">
				<!--
					While fading out, the header transition needs to be the same duration as the transition
					on the block so that the header text doesn't change while the block is fading out.
				-->
				<template v-if="showHeader">
					<transition name="cog-fade" mode="out-in" :duration="leaving ? 500 : 0">
						<h2 v-if="isPaymentHeader" key="1" class="cog-heading">
							{{ $resource('payment') }}
						</h2>
						<h2 v-else key="2" class="cog-heading">
							{{ $resource('credit-card-authorization') }}
						</h2>
					</transition>
				</template>
				<div class="cog-row">
					<div class="cog-col cog-col--24">
						<section class="cog-section__inner cog-section__inner--background" :class="{ 'cog-payment-error-bg': paymentUnavailableHighlighted }">
							<div v-if="$slots.paymentDetails && order.Id" class="cog-row">
								<slot name="paymentDetails"></slot>
							</div>
							<div class="cog-row cog-payment__content-row">
								<template v-if="paymentProcessor && flags.processPayment">
									<visible-transition
										@after-enter="forceStripeReflow"
									>
										<div v-show="showPayment" class="cog-col cog-col--12">
											<slot name="payment" :save-card="saveCard"></slot>
										</div>
									</visible-transition>
								</template>
								<c-order-details
									v-if="showOrder"
									:order="order"
									:show-line-items="showLineItems"
									:show-sub-total="showSubTotal"
									:readonly="readonly"
									:order-amount-label="orderAmountLabel"
									:cols="showPayment && $scopedSlots.payment && flags.processPayment ? 12 : 24"
									:include-fees="includeFees"
								/>
							</div>
						</section>
					</div>
				</div>
			</div>
		</section>
	</visible-transition>
</template>

<script>
	import 'src/style/_motion.scss';
	import 'src/style/_section.scss';
	import Col from '../Col.vue';
	import COrderDetails from './OrderDetails.vue';

	export default {
		name: 'COrder',
		components: { COrderDetails },
		mixins: [Col],
		inject: ['$resource', 'flags'],
		props: {
			entry: { type: Object, required: true },
			order: { type: Object, required: true },
			showLineItems: { type: Boolean, default: false },
			showSubTotal: { type: Boolean, default: false },
			showHeader: { type: Boolean, default: true },
			orderAmountLabel: { type: String, default: null },
			saveCard: { type: Boolean, default: false },
			paymentProcessor: { type: String, default: null },
			readonly: { type: Boolean, default: false },
			processPayment: { type: Boolean, default: true },
			hasPayableAction: { type: Boolean, default: true },
			includeFees: { type: Boolean, default: true },
			paymentUnavailableHighlighted: { type: Boolean, default: false }
		},
		data() {
			return {
				leaving: false
			};
		},
		computed: {
			showPayment() {
				if (!this.paymentProcessor)
					return false;

				if (this.flags.workflowEnabled)
					return this.processPayment;

				return (this.saveCard && !this.readonly && this.flags.processPayment) || (this.order.IsOpen && this.order.AmountPaid === 0 && this.order.AmountDue > 0 && this.entry.Require_Payment);
			},
			showOrder() {
				return (this.order.LineItems && this.order.LineItems.length > 0) || this.order.OrderAmount > 0;
			},
			isPaymentHeader() {
				if (!this.hasPayableAction && this.saveCard)
					return false;
				else
					return this.showOrder && (this.entry.Require_Payment || !this.saveCard || !this.flags.processPayment);
			}
		},
		methods: {
			// 20003: Safari on iOS doesn't properly render/paint the stripe cc input fields on the iframe after a transition,
			// these two methods trigger the reflow of the strip input elements and is called
			// on the after-enter hook on the visible transition for the payment block
			forceReflow(selector) {
				const el = document.querySelector(selector);
				if (el) {
					const initialDisplay = el.style.display;

					el.style.display = 'none';
					requestAnimationFrame(() => {
						el.style.display = initialDisplay;
					});
				}
			},
			forceStripeReflow() {
				this.forceReflow('#cog-card-number');
				this.forceReflow('#cog-card-expiry');
				this.forceReflow('#cog-card-cvv');
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-payment__payment-details {
		margin-top: $gutter-half;
		margin-bottom: $gutter-two-thirds;

		td,
		th,
		p {
			margin: 0;

			// Chrome renders decimal numbers differently depending on the element
			// Therefore, px needed to substitute em in this situation
			padding-top: 3px;
			padding-bottom: 3px;
		}

		th {
			width: 4em;
			padding-right: 1em;
			text-align: left;

			&::after {
				content: ':';
			}
		}
	}

	// Compensate for the vertical space added to fields which is not wanted here.
	.cog-payment__content-row {
		margin-top: calc(var(--gutter) * -.5);
		margin-bottom: calc(var(--gutter) * -.5);

		.cog-form-payment-error--paypal {
			width: 100%;
			margin-top: calc(var(--gutter) * .5);
		}
	}

	@include at-root(-1) {

		.cog-payment--square .cog-col--12 {
			flex-basis: 260px;
		}
	}
}
</style>